import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import "./styles/Partnerdetail.css";
import "./styles/OwlLabs.css";
import owlLabsLogo from "../../assets/images/partners/OwlLabs.jpg";

const meetingFeatures = [
  { icon: "🎯", title: "360° Room Coverage", desc: "Meeting Owl's 360-degree camera captures every participant in the room simultaneously — with intelligent face detection automatically zooming on the active speaker." },
  { icon: "🔊", title: "Spatial Audio Intelligence", desc: "Built-in microphone array picks up voices from every direction — with AI noise suppression filtering background noise and keyboard clicks for crystal-clear audio." },
  { icon: "🤝", title: "Hybrid Meeting Equity", desc: "Owl Labs' Meeting Equity AI ensures remote participants feel equally present — detecting non-verbal cues and surfacing remote participants' reactions alongside in-room attendees." },
  { icon: "☁️", title: "Seamless Platform Integration", desc: "Native integration with Zoom, Microsoft Teams, Google Meet, Cisco Webex, and RingCentral — plug and play without driver installation or IT configuration required." },
];

const aiCapabilities = [
  { icon: "👁️", title: "Speaker Focus Tracking", desc: "Automatically zooms and focuses on the active speaker as they move around the room — ensuring remote participants always see who is speaking." },
  { icon: "🧠", title: "Intelligent Framing", desc: "AI adjusts the composite view in real time — showing the full room panorama alongside speaker close-ups for optimal remote comprehension." },
  { icon: "🎙️", title: "Voice Isolation AI", desc: "Isolates human voices from HVAC noise, side conversations, and ambient room sounds — ensuring only meeting-relevant audio reaches remote participants." },
  { icon: "📊", title: "Meeting Analytics", desc: "Owl Intelligence Platform tracks meeting participation, room utilization, and engagement metrics — providing data to optimize your hybrid meeting room strategy." },
  { icon: "🔄", title: "Auto-Firmware Updates", desc: "Owl devices automatically update with new AI features — your investment improves over time without requiring manual IT intervention." },
  { icon: "🛡️", title: "Enterprise Security", desc: "SOC 2 Type II certified, ISO 27001 aligned, and GDPR compliant — with encrypted video streams, role-based admin access, and no video storage on the device." },
];

const products = [
  { img: "🦉", name: "Meeting Owl Pro", tagline: "360° AI Smart Camera", desc: "The flagship hybrid meeting device — a standalone 360-degree camera, microphone, and speaker in one device. Plug into any laptop or UC platform for instant AI-powered meeting room capability without complex AV installation." },
  { img: "🦉", name: "Meeting Owl 3", tagline: "Scalable Room Camera", desc: "Owl Labs' third-generation smart camera with improved 1080p image quality, expanded 18-foot microphone range, and enhanced AI face detection for medium conference rooms hosting 6–12 participants." },
  { img: "📺", name: "Owl Bar", tagline: "Soundbar Camera All-in-One", desc: "Purpose-built for huddle rooms and small conference rooms — the Owl Bar combines a wide-angle camera, speaker bar, and microphone array in a sleek, TV-mounted form factor for Microsoft Teams and Zoom Rooms." },
  { img: "🖥️", name: "Meeting HQ", tagline: "Large Conference Solution", desc: "Owl Labs' enterprise conference room solution for large boardrooms and training rooms — combining wide-angle camera coverage, advanced audio processing, and Owl Intelligence analytics for rooms up to 30 people." },
  { img: "📡", name: "Owl Intelligence", tagline: "Analytics Platform", desc: "Cloud-based analytics platform providing room utilization data, meeting frequency trends, and AI model performance metrics — helping organizations optimize their hybrid meeting room investments with actual usage data." },
  { img: "⚙️", name: "OWL Connect", tagline: "Accessory Ecosystem", desc: "Owl Connect links multiple Meeting Owl devices in large rooms for expanded coverage — with automatic stitching of panoramic views creating a seamless meeting experience in non-standard room configurations." },
];

const journeySteps = [
  { icon: "📐", title: "Room Assessment", desc: "ALG Data Guard assesses your conference room portfolio — dimensions, lighting, existing AV, and ceiling height — recommending the optimal Owl Labs device per room type." },
  { icon: "🛒", title: "Solution Design", desc: "Tailored Owl Labs device selection and quantity recommendation aligned to your room inventory, UC platform (Teams/Zoom/Google), and meeting frequency patterns." },
  { icon: "🔌", title: "Plug & Play Setup", desc: "Owl Labs devices are designed for zero-IT deployment — USB connection to room compute, network registration, and platform assignment in under 15 minutes per room." },
  { icon: "🤖", title: "Owl Intelligence", desc: "Enable the cloud analytics dashboard for room utilization reporting, meeting quality monitoring, and AI performance visibility across your entire Owl Labs deployment." },
];

const hybridStats = [
  { val: "85%", label: "of meetings are now hybrid" },
  { val: "3x", label: "more productive meetings with AI cameras" },
  { val: "40%", label: "reduction in travel costs" },
  { val: "92%", label: "employee satisfaction with Owl Labs" },
];

const industries = [
  { icon: "🏢", name: "Corporate Enterprise", desc: "Hybrid meeting equity across global headquarters, regional offices, and remote workers — with Owl Intelligence analytics optimizing room investments." },
  { icon: "🎓", name: "Higher Education", desc: "Hybrid learning rooms enabling students attending in-person and remotely to have equivalent participation experiences in seminars and small group sessions." },
  { icon: "🏥", name: "Healthcare", desc: "HIPAA-compliant video conferencing for telehealth consultations, multi-site clinical team meetings, and remote specialist consultations in patient care rooms." },
  { icon: "🏛️", name: "Government", desc: "Hybrid government meeting rooms for inter-agency collaboration, public hearings with remote participants, and distributed team coordination." },
  { icon: "⚖️", name: "Legal & Professional", desc: "Client consultation rooms, deposition suites, and partner meeting spaces with premium AI camera quality for sensitive professional interactions." },
  { icon: "🏭", name: "Manufacturing", desc: "Hybrid engineering review meetings, production floor video conferencing, and multi-site manufacturing coordination through rugged, easy-to-use Meeting Owl devices." },
  { icon: "💼", name: "Professional Services", desc: "Client presentation rooms, consulting team collaboration spaces, and partner meeting rooms delivering premium meeting quality for client-facing interactions." },
  { icon: "🔬", name: "Research & Science", desc: "Research collaboration rooms enabling distributed team science — with high-quality video for microscopy demonstrations, whiteboard sessions, and lab equipment presentations." },
];

const benefits = [
  { icon: "⚡", title: "Deploy in Hours, Not Days", desc: "Owl Labs' plug-and-play design eliminates complex AV installation — rooms go live in 15 minutes with USB connection and network registration, no cabling required." },
  { icon: "📊", title: "Meeting Room Analytics", desc: "Owl Intelligence provides the data organizations need to make informed decisions — which rooms are underutilized, which are always booked, and how meeting quality varies by location." },
  { icon: "🔄", title: "Platform Agnostic", desc: "Owl Labs devices work with any UC platform — Zoom, Teams, Google Meet, Webex — without vendor lock-in, protecting your investment across platform changes." },
  { icon: "🤝", title: "Hybrid Meeting Equity", desc: "Owl Labs' core mission is ensuring remote participants feel as present as in-room attendees — the Meeting Equity AI actively promotes balanced participation in every meeting." },
  { icon: "🛡️", title: "Enterprise-Grade Security", desc: "SOC 2 certified, no video stored on device, encrypted streams, and centralized device management through Owl Intelligence for enterprise security compliance." },
];

const faqData = [
  { q: "What makes Owl Labs different from standard conference room cameras like Logitech or Poly?", a: "Owl Labs differentiates through its AI-powered 360-degree meeting experience and hybrid meeting equity focus. Standard conference cameras are unidirectional — pointing at a fixed view of the room — meaning remote participants see a wide shot of the whole table or a manually panned view. Meeting Owl's 360-degree camera sees the entire room simultaneously, with AI automatically creating a composite view that shows both the full room panorama and speaker close-ups. When someone speaks, the AI automatically zooms on them — creating an experience similar to a broadcast production where cameras follow the speaker. This dramatically improves the remote meeting experience compared to a fixed-angle camera where remote participants see people from across the room. The Meeting HQ and Owl Bar extend this technology to different room sizes and form factors." },
  { q: "What is Meeting Equity AI and why does it matter for hybrid teams?", a: "Meeting Equity AI is Owl Labs' term for the technology and design philosophy ensuring remote meeting participants have an equivalent experience to in-room participants. In traditional hybrid meetings, the experience is fundamentally unequal — in-room participants benefit from natural body language, sidebar conversations, and visual whiteboard content, while remote participants see a static camera view of the room. Owl Labs addresses this through: 360-degree camera providing awareness of all in-room participants rather than a fixed crop; intelligent speaker tracking so remote participants always see who is speaking regardless of where they sit; integration with collaboration platforms to surface remote participants' reactions and nonverbal cues; and room audio design ensuring remote voices are heard as clearly as in-room voices. Organizations with strong hybrid work policies find Meeting Equity AI critical for maintaining remote employee engagement and ensuring distributed team members have genuine participation in meetings." },
  { q: "How does Owl Intelligence analytics help organizations optimize meeting rooms?", a: "Owl Intelligence is Owl Labs' cloud analytics platform providing data-driven insights about how meeting rooms are actually being used versus how organizations think they're being used. Key analytics include: Room utilization rates by room, by day of week, and by time of day — often revealing that organizations have significantly over-provisioned or under-provisioned certain room sizes. Meeting participation breakdown between in-room and remote participants — quantifying the scale of hybrid meeting adoption. Meeting Owl device health and performance metrics — identifying when devices need firmware updates or have connectivity issues. Room occupancy trends — supporting real estate decisions about office space and room portfolio composition. For organizations planning hybrid work policies or making real estate decisions, Owl Intelligence provides the actual data rather than anecdotal information from employee surveys." },
  { q: "What UC platforms does Owl Labs work with?", a: "Meeting Owl devices work as standard USB video and audio devices — compatible with any video conferencing software that accepts standard webcam and microphone input. Primary certified integrations include: Zoom (Certified for Zoom Rooms and standard Zoom meetings), Microsoft Teams (Teams Rooms certified and standard Teams meetings), Google Meet (Google Meet Hardware certified), Cisco Webex (Webex certified), and RingCentral (RingCentral Video certified). Because Owl devices appear as standard USB devices to the host computer, they also work with Slack, BlueJeans, Lifesize, Skype, GoToMeeting, and any other video platform — even if not explicitly certified. ALG Data Guard configures Owl Labs devices optimally for your primary UC platform and ensures proper network requirements are met for optimal streaming quality." },
  { q: "How does ALG Data Guard deploy and support Owl Labs devices?", a: "ALG Data Guard provides complete Owl Labs deployment services: conference room assessment documenting room dimensions, current AV equipment, lighting conditions, and meeting frequency patterns — informing device selection for each room type. Solution design recommending Meeting Owl Pro, Meeting Owl 3, Owl Bar, or Meeting HQ based on room size, participant count, and UC platform. Device provisioning — registering all devices in Owl Intelligence, configuring organizational settings, enabling analytics, and assigning devices to appropriate location hierarchies. Platform integration — configuring Owl Labs devices as the primary camera/audio source in your Zoom Rooms, Teams Rooms, or Google Meet hardware deployment. End-user training for both in-room participants and IT administrators managing the Owl Intelligence dashboard. Ongoing support includes device monitoring through Owl Intelligence, firmware management, replacement coordination for failed devices, and expansion support when adding new rooms." },
];

const ArrowIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12h14M13 5l7 7-7 7"/></svg>
);
const ChevronDown = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6"/></svg>
);

export default function OwlLabs() {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);

  return (
    <div className="ol-page pd-page">

      {/* ── Identity Band ── */}
      <div className="pd-identity-band">
        <div className="pd-identity-inner">
          <img src={owlLabsLogo} alt="Owl Labs" className="pd-partner-logo" />
          <div className="pd-identity-divider" aria-hidden="true" />
          <div className="pd-identity-text">
            <div className="pd-eyebrow pd-eyebrow--dark">
              <span className="pd-live-dot pd-live-dot--dark" />
              Hybrid Meeting Technology Partner
            </div>
            <h1 className="pd-identity-name">ALG Data Guard × Owl Labs</h1>
            <p className="pd-identity-desc">
              Owl Labs is the creator of the Meeting Owl — the world's first 360-degree AI smart camera designed specifically for hybrid meetings. Where traditional conference cameras create an unequal experience between in-room and remote participants, Owl Labs' patented 360-degree capture and Meeting Equity AI ensures every participant in every meeting feels equally present, heard, and seen. Trusted by thousands of organizations globally, Owl Labs is redefining what hybrid collaboration looks like. ALG Data Guard is an authorized Owl Labs reseller providing device selection, deployment, and managed meeting room services.
            </p>
          </div>
        </div>
      </div>

      {/* ── Hero ── */}
      <section className="ol-hero">
        <div className="ol-hero__blob-1" aria-hidden="true" />
        <div className="ol-hero__blob-2" aria-hidden="true" />
        <div className="ol-hero__orbit" aria-hidden="true">
          <div className="ol-hero__orbit-ring ol-hero__orbit-ring--1" />
          <div className="ol-hero__orbit-ring ol-hero__orbit-ring--2" />
          <div className="ol-hero__orbit-ring ol-hero__orbit-ring--3" />
          <div className="ol-hero__device">🦉</div>
          {[
            { top:"10%", left:"70%", icon:"🎤" },
            { top:"85%", left:"65%", icon:"📷" },
            { top:"50%", left:"90%", icon:"💻" },
          ].map(d => (
            <div key={d.icon} className="ol-hero__orbit-dot" style={{ top:d.top, left:d.left, transform:"translate(-50%,-50%)" }}>{d.icon}</div>
          ))}
        </div>
        <div className="ol-hero__inner">
          <div>
            <div className="ol-pill ol-pill--cream"><span className="ol-spark" /> Owl Labs Authorized Partner</div>
            <h2 className="ol-hero__title">
              Every Meeting.<br />Every Person.<br /><em>Equally Present.</em>
            </h2>
            <p className="ol-hero__body">
              Owl Labs' 360-degree AI smart cameras transform ordinary conference rooms into hybrid meeting spaces where every participant — in-room or remote — feels equally engaged. Meeting Equity AI automatically frames speakers, eliminates background noise, and delivers broadcast-quality video from a single plug-and-play device. ALG Data Guard deploys Owl Labs across your conference room portfolio.
            </p>
            <div className="ol-hero__actions">
              <button className="ol-btn ol-btn--white" onClick={() => navigate("/contact")}>
                Deploy Owl Labs <ArrowIcon />
              </button>
              <button className="ol-btn ol-btn--ghost-white" onClick={() => navigate("/contact")}>
                Room Assessment
              </button>
            </div>
            <div className="ol-hero__social-proof">
              <span className="ol-hero__stars">★★★★★</span>
              <span className="ol-hero__proof-text">Trusted by 10,000+ organizations • SOC 2 Certified</span>
            </div>
          </div>
          {/* Spacer for orbit device visual */}
          <div />
        </div>
      </section>

      {/* ── Smart Meeting Rooms ── */}
      <section className="ol-meeting">
        <div className="ol-shell">
          <div className="ol-meeting__inner">
            <img
              src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?auto=format&fit=crop&w=900&q=80"
              alt="Smart hybrid meeting room"
              className="ol-meeting__img"
            />
            <div>
              <div className="ol-pill ol-pill--purple"><span className="ol-spark" /> Smart Meeting Rooms</div>
              <h2 className="ol-title ol-meeting__title">Conference Rooms Designed for How You Actually Work</h2>
              <p className="ol-sub ol-meeting__body">
                The modern conference room isn't just for in-person meetings — it's a hybrid collaboration hub connecting distributed teams. Owl Labs Meeting Owl transforms any room with a table into an AI-powered meeting space where remote participants see everything happening in the room, hear every voice clearly, and feel genuinely present in the conversation.
              </p>
              <p className="ol-sub" style={{ marginBottom:"1.8rem" }}>
                ALG Data Guard assesses your existing conference room portfolio and recommends the right Owl Labs device for each room size — ensuring your investment in hybrid meeting technology delivers measurable meeting quality improvement.
              </p>
              <div className="ol-meeting__features">
                {meetingFeatures.map(f => (
                  <div key={f.title} className="ol-meet-feat">
                    <span className="ol-meet-feat__icon">{f.icon}</span>
                    <div>
                      <div className="ol-meet-feat__title">{f.title}</div>
                      <p className="ol-meet-feat__desc">{f.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ marginTop:"2rem" }}>
                <button className="ol-btn ol-btn--purple" onClick={() => navigate("/contact")}>
                  Assess My Meeting Rooms <ArrowIcon />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── AI Camera Technology ── */}
      <section className="ol-ai">
        <div className="ol-shell">
          <div className="ol-ai__inner">
            <img
              src="https://images.unsplash.com/photo-1587825140708-dfaf72ae4b04?auto=format&fit=crop&w=900&q=80"
              alt="AI-powered video conferencing"
              className="ol-ai__img"
            />
            <div>
              <div className="ol-pill ol-pill--light"><span className="ol-spark" /> AI Camera Technology</div>
              <h2 className="ol-ai__title">The Intelligence Behind Every Meeting</h2>
              <p className="ol-ai__body">
                Owl Labs' AI isn't a marketing feature — it's the foundation of why Meeting Owl creates a fundamentally different meeting experience. Running locally on the device without cloud processing dependency, Owl Labs' AI continuously analyzes audio and video to create the optimal hybrid meeting view in real time.
              </p>
              <div className="ol-ai__capabilities">
                {aiCapabilities.map(cap => (
                  <div key={cap.title} className="ol-ai-cap">
                    <span className="ol-ai-cap__icon">{cap.icon}</span>
                    <div className="ol-ai-cap__title">{cap.title}</div>
                    <p className="ol-ai-cap__desc">{cap.desc}</p>
                  </div>
                ))}
              </div>
              <div style={{ marginTop:"2rem" }}>
                <button className="ol-btn ol-btn--white" onClick={() => navigate("/contact")}>
                  Explore Meeting Owl AI <ArrowIcon />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Hybrid Workforce ── */}
      <section className="ol-hybrid">
        <div className="ol-shell">
          <div className="ol-hybrid__inner">
            <div>
              <div className="ol-pill ol-pill--purple"><span className="ol-spark" /> Hybrid Workforce</div>
              <h2 className="ol-title ol-hybrid__title">Built for How Modern Teams Actually Collaborate</h2>
              <p className="ol-sub ol-hybrid__body">
                Hybrid work is no longer a temporary accommodation — it's the permanent operating model for most organizations. Owl Labs' entire product line is designed around the reality that every meeting now includes both in-room and remote participants, and creating equity between these two groups is the single most important factor in hybrid meeting success.
              </p>
              <div className="ol-hybrid__stats">
                {hybridStats.map(s => (
                  <div key={s.val} className="ol-hybrid-stat">
                    <div className="ol-hybrid-stat__val">{s.val}</div>
                    <div className="ol-hybrid-stat__label">{s.label}</div>
                  </div>
                ))}
              </div>
              <button className="ol-btn ol-btn--purple" onClick={() => navigate("/contact")}>
                Build Your Hybrid Rooms <ArrowIcon />
              </button>
            </div>
            <img
              src="https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=900&q=80"
              alt="Hybrid workforce collaboration"
              className="ol-hybrid__img"
            />
          </div>
        </div>
      </section>

      {/* ── Product Ecosystem ── */}
      <section className="ol-ecosystem">
        <div className="ol-shell">
          <div className="ol-ecosystem__head">
            <div className="ol-pill ol-pill--light" style={{ margin:"0 auto 1rem" }}><span className="ol-spark" /> Device Ecosystem</div>
            <h2 className="ol-title">The Right Owl Labs Device for Every Room</h2>
            <p className="ol-ecosystem__sub">Owl Labs' product line covers every conference room size — from huddle rooms to executive boardrooms — with AI-powered 360-degree collaboration built into every device.</p>
          </div>
          <div className="ol-ecosystem__grid">
            {products.map(p => (
              <div key={p.name} className="ol-product-card">
                <span className="ol-product-card__img">{p.img}</span>
                <div className="ol-product-card__name">{p.name}</div>
                <div className="ol-product-card__tagline">{p.tagline}</div>
                <p className="ol-product-card__desc">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Meeting Journey ── */}
      <section className="ol-journey">
        <div className="ol-shell">
          <div className="ol-journey__head">
            <div className="ol-pill ol-pill--purple" style={{ margin:"0 auto 1rem" }}><span className="ol-spark" /> Deployment Journey</div>
            <h2 className="ol-title">From Assessment to Activated Rooms</h2>
            <p className="ol-sub" style={{ margin:"0 auto", maxWidth:"560px" }}>ALG Data Guard handles every step of your Owl Labs deployment — from initial room assessment through Owl Intelligence analytics activation.</p>
          </div>
          <div className="ol-journey__steps">
            {journeySteps.map(step => (
              <div key={step.title} className="ol-journey-step">
                <div className="ol-journey-step__icon">{step.icon}</div>
                <h3 className="ol-journey-step__title">{step.title}</h3>
                <p className="ol-journey-step__desc">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Industries ── */}
      <section className="ol-industries">
        <div className="ol-shell">
          <div className="ol-industries__head">
            <div className="ol-pill ol-pill--purple"><span className="ol-spark" /> Industries</div>
            <h2 className="ol-title">Hybrid Meetings Across Every Sector</h2>
            <p className="ol-sub" style={{ margin:"0 auto", maxWidth:"560px" }}>Owl Labs delivers hybrid meeting equity for organizations in every industry — with compliance-appropriate configurations and deployment patterns for regulated environments.</p>
          </div>
          <div className="ol-industries__grid">
            {industries.map(ind => (
              <div key={ind.name} className="ol-ind-card">
                <span className="ol-ind-card__icon">{ind.icon}</span>
                <h3 className="ol-ind-card__name">{ind.name}</h3>
                <p className="ol-ind-card__desc">{ind.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Workplace Transformation ── */}
      <section className="ol-transform">
        <div className="ol-shell">
          <div className="ol-transform__inner">
            <div>
              <div className="ol-pill ol-pill--light"><span className="ol-spark" /> Business Benefits</div>
              <h2 className="ol-transform__title">
                Meetings That Work for <span>Everyone</span>
              </h2>
              <p className="ol-transform__body">
                Owl Labs isn't just a camera purchase — it's a fundamental improvement in how your organization collaborates. Organizations that deploy Owl Labs across their meeting room portfolio report measurable improvements in remote employee engagement, meeting participation equity, and overall satisfaction with hybrid work policies.
              </p>
              <div className="ol-transform__benefits">
                {benefits.map(b => (
                  <div key={b.title} className="ol-benefit">
                    <span className="ol-benefit__icon">{b.icon}</span>
                    <div>
                      <div className="ol-benefit__title">{b.title}</div>
                      <p className="ol-benefit__desc">{b.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <img
              src="https://images.unsplash.com/photo-1551434678-e076c223a692?auto=format&fit=crop&w=900&q=80"
              alt="Workplace transformation through Owl Labs"
              className="ol-transform__img"
            />
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="ol-faq">
        <div className="ol-shell">
          <div className="ol-faq__head">
            <div className="ol-pill ol-pill--purple" style={{ margin:"0 auto 1rem" }}><span className="ol-spark" /> FAQs</div>
            <h2 className="ol-title">Owl Labs Questions, Answered</h2>
            <p className="ol-sub" style={{ margin:"0 auto", maxWidth:"560px" }}>Expert answers about Owl Labs Meeting Owl, Meeting Equity AI, Owl Intelligence analytics, and ALG Data Guard's deployment services.</p>
          </div>
          <div className="ol-faq__list">
            {faqData.map((faq, i) => (
              <div key={i} className={`ol-faq-item${openFaq === i ? " open" : ""}`}>
                <button className="ol-faq-item__trigger" onClick={() => setOpenFaq(openFaq === i ? null : i)} aria-expanded={openFaq === i}>
                  <span className="ol-faq-item__q">{faq.q}</span>
                  <span className="ol-faq-item__arrow"><ChevronDown /></span>
                </button>
                <div className="ol-faq-item__answer"><div className="ol-faq-item__answer-inner">{faq.a}</div></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="ol-cta">
        <div className="ol-cta__inner">
          <div className="ol-pill ol-pill--cream" style={{ margin:"0 auto 1.2rem" }}><span className="ol-spark" /> Get Started</div>
          <h2 className="ol-cta__title">Transform Every Meeting Room Into a Hybrid Hub</h2>
          <p className="ol-cta__sub">Whether you're equipping one boardroom or deploying Owl Labs across 50 conference rooms — ALG Data Guard handles everything from room assessment through Owl Intelligence analytics activation. Give every participant the meeting experience they deserve.</p>
          <div className="ol-cta__actions">
            <button className="ol-btn ol-btn--white" onClick={() => navigate("/contact")}>
              Deploy Owl Labs Now <ArrowIcon />
            </button>
            <button className="ol-btn ol-btn--ghost-white" onClick={() => navigate("/contact")}>
              Request a Room Assessment
            </button>
          </div>
        </div>
      </section>

    </div>
  );
}
